import {
  TrendingUp,
  Calendar,
  DollarSign,
  AlertTriangle,
  CalendarDays,
} from "lucide-react";
import { useState, lazy, Suspense, useMemo } from "react";
import { Link } from "react-router-dom";
import { AddSubscriptionDialog } from "@/components/AddSubscriptionDialog";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import { MonthlySpendDetailsDialog } from "@/components/MonthlySpendDetailsDialog";
import { PaygFilterToggle } from "@/components/PaygFilterToggle";
import { Button } from "@/components/ui/button";
import { YearlySpendDetailsDialog } from "@/components/YearlySpendDetailsDialog";
const ReconciliationModal = lazy(() =>
  import("@/components/ReconciliationModal").then((module) => ({
    default: module.ReconciliationModal,
  })),
);
const NextPaymentDetails = lazy(() =>
  import("@/components/NextPaymentDetails").then((module) => ({
    default: module.NextPaymentDetails,
  })),
);
const MonthNavigator = lazy(() =>
  import("@/components/MonthNavigator").then((module) => ({
    default: module.MonthNavigator,
  })),
);
import { useDashboardSummary } from "@/hooks/useDashboardSummary";
import { useDialogState } from "@/hooks/useDialogState";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { getDisplayAmount } from "@/lib/currency";
import { databaseSubscriptionToSubscription } from "@/lib/dataTransformers";
import { type ViewingPeriod } from "@/lib/periodStorageUtils";

interface DashboardSummaryProps {
  currentPeriod: ViewingPeriod;
  onPeriodChange: (period: ViewingPeriod) => void;
  onNavigatePeriod: (direction: "prev" | "next") => void;
  onSetToCurrentMonth: () => void;
  onSetToNextMonth: () => void;
  includePaygInStats: boolean;
  onIncludePaygInStatsChange: (include: boolean) => void;
}

export const DashboardSummary = ({
  currentPeriod,
  onPeriodChange,
  onNavigatePeriod,
  onSetToCurrentMonth,
  onSetToNextMonth,
  includePaygInStats,
  onIncludePaygInStatsChange,
}: DashboardSummaryProps) => {
  const { subscriptions, isLoading } = useSubscriptions();
  const { payments } = usePaymentHistory();
  const [isReconciliationModalOpen, setIsReconciliationModalOpen] =
    useState(false);
  const [isNextDetailsOpen, setIsNextDetailsOpen] = useState(false);
  const [isUpgradePromptOpen, setIsUpgradePromptOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);

  const monthlyDialog = useDialogState();
  const yearlyDialog = useDialogState();
  const { canConvertCurrency } = useFeatureGate();

  // Use the extracted dashboard calculations hook
  // activeCount is returned by the hook but not currently used here; rename
  // to an ignored identifier to satisfy the lint rule for unused vars.
  const {
    monthlyTotal,
    yearlyTotal,
    nextPayment,
    pendingCount,
    activeCount: ignoredActiveCount,
    excludedCurrencyCount,
    primaryCurrency,
    totalCurrency,
  } = useDashboardSummary(subscriptions, currentPeriod, includePaygInStats);

  const subscriptionsForDetails = useMemo(() => {
    if (canConvertCurrency || !primaryCurrency) return subscriptions;

    return subscriptions.filter(
      (subscription) =>
        subscription.status === "active" &&
        subscription.currency === primaryCurrency,
    );
  }, [canConvertCurrency, primaryCurrency, subscriptions]);

  const selectedSubscription = useMemo(() => {
    if (!selectedSubscriptionId) return undefined;
    const dbSub = subscriptions.find((s) => s.id === selectedSubscriptionId);
    if (!dbSub) return undefined;
    return databaseSubscriptionToSubscription(dbSub);
  }, [selectedSubscriptionId, subscriptions]);

  const handleEditSubscription = (subscriptionId: string) => {
    // Close breakdown dialogs first
    monthlyDialog.closeDialog();
    yearlyDialog.closeDialog();

    // Small delay to ensure dialogs are closed before opening edit dialog
    setTimeout(() => {
      setSelectedSubscriptionId(subscriptionId);
    }, 100);
  };

  const handleEditComplete = () => {
    setSelectedSubscriptionId(null);
  };

  const formatCurrency = (amount: number) => {
    return getDisplayAmount(amount, totalCurrency, totalCurrency).formatted;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="py-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-foreground">
            Your Subscriptions
          </h1>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const getUrgencyColor = (days: number) => {
    if (days < 0) return "text-destructive";
    if (days <= 3) return "text-warning";
    return "text-muted-foreground";
  };

  const periodName = new Date(
    currentPeriod.year,
    currentPeriod.month,
  ).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const isCurrentPeriod =
    currentPeriod.month === currentMonth && currentPeriod.year === currentYear;
  const isPastPeriod =
    currentPeriod.year < currentYear ||
    (currentPeriod.year === currentYear && currentPeriod.month < currentMonth);

  return (
    <div className="space-y-6">
      {/* Month Navigator */}
      <div className="glass-card p-4">
        <Suspense
          fallback={
            <div className="flex h-16 items-center justify-center text-muted-foreground">
              Loading navigation...
            </div>
          }
        >
          <MonthNavigator
            currentPeriod={currentPeriod}
            onPeriodChange={onPeriodChange}
            onNavigatePeriod={onNavigatePeriod}
            onSetToCurrentMonth={onSetToCurrentMonth}
            onSetToNextMonth={onSetToNextMonth}
          />
        </Suspense>
      </div>

      {/* Welcome Header with Period Context */}
      <div className="text-center">
        <div className="mb-2 flex items-center justify-center gap-2">
          <h1 className="text-3xl font-bold text-foreground">{periodName}</h1>
          {!isCurrentPeriod && (
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                isPastPeriod
                  ? "bg-muted text-muted-foreground"
                  : "bg-primary/10 text-primary"
              }`}
            >
              {isPastPeriod ? "Past" : "Forecast"}
            </span>
          )}
        </div>
        <p className="text-muted-foreground">
          Verify payments manually to stay aware of your spending
        </p>
      </div>

      {/* Calendar View Promotion */}
      <div className="glass-card border-l-4 border-primary/30 bg-primary/5 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-primary" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                See the big picture
              </h3>
              <p className="text-xs text-muted-foreground">
                Visualize all your payments in Calendar View to spot patterns
                and plan ahead
              </p>
            </div>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/calendar">
              <CalendarDays className="mr-2 h-4 w-4" />
              Open Calendar
            </Link>
          </Button>
        </div>
      </div>

      {/* Summary Cards - Reordered: Pending, Next Payment, Monthly, Yearly */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* 1. Pending Actions - MOST IMPORTANT */}
        <div
          className={`glass-card hover-lift hover:shadow-strong group cursor-pointer p-6 transition-all duration-200 ${
            pendingCount > 0
              ? "border-2 border-destructive/30 bg-destructive/5"
              : ""
          }`}
          onClick={() => setIsReconciliationModalOpen(true)}
        >
          <div className="mb-2 flex items-center space-x-3">
            <div
              className={`rounded-lg p-2 transition-colors ${
                pendingCount > 0
                  ? "bg-destructive/20 group-hover:bg-destructive/30"
                  : "bg-destructive/10 group-hover:bg-destructive/20"
              }`}
            >
              <AlertTriangle
                className={`h-5 w-5 ${
                  pendingCount > 0
                    ? "animate-pulse text-destructive"
                    : "text-destructive"
                }`}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Pending Verification
              </p>
              <div className="flex items-center space-x-2">
                <p className="text-3xl font-bold text-foreground">
                  {pendingCount}
                </p>
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {pendingCount > 0 ? (
              <span className="font-medium text-destructive">
                Verify {pendingCount} payment{pendingCount > 1 ? "s" : ""} now
              </span>
            ) : (
              <span className="text-green-600">All caught up! 🎉</span>
            )}
          </div>
        </div>

        {/* 2. Next Payment */}
        <div
          className="glass-card hover-lift cursor-pointer p-6 transition-all"
          onClick={() => nextPayment && setIsNextDetailsOpen(true)}
        >
          <div className="mb-2 flex items-center space-x-3">
            <div className="rounded-lg bg-warning/10 p-2">
              <Calendar className="h-5 w-5 text-warning" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                Next Payment
              </p>
              {nextPayment ? (
                <>
                  <p className="truncate text-lg font-semibold text-foreground">
                    {nextPayment.subscription.name}
                  </p>
                  <p
                    className={`text-xs font-medium ${getUrgencyColor(nextPayment.daysUntil)}`}
                  >
                    {nextPayment.daysUntil < 0
                      ? `${Math.abs(nextPayment.daysUntil)} days overdue`
                      : nextPayment.daysUntil === 0
                        ? "Due today"
                        : `in ${nextPayment.daysUntil} day${nextPayment.daysUntil !== 1 ? "s" : ""}`}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">None scheduled</p>
              )}
            </div>
          </div>
        </div>

        {/* 3. Monthly Total */}
        <div
          className="glass-card hover-lift cursor-pointer p-6 transition-shadow hover:shadow-lg"
          onClick={monthlyDialog.openDialog}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  This Month
                </p>
                <p className="amount-text text-3xl font-bold text-foreground">
                  {formatCurrency(monthlyTotal)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">View breakdown</span>
            <span className="font-medium text-primary">→</span>
          </div>
        </div>

        {/* 4. Yearly Total */}
        <div
          className="glass-card hover-lift cursor-pointer p-6 transition-shadow hover:shadow-lg"
          onClick={yearlyDialog.openDialog}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-accent-blue/10 p-2">
                <TrendingUp className="h-5 w-5 text-accent-blue" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {currentPeriod.year} Total
                </p>
                <p className="amount-text text-3xl font-bold text-foreground">
                  {formatCurrency(yearlyTotal)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">View breakdown</span>
            <span className="font-medium text-primary">→</span>
          </div>
        </div>
      </div>

      {excludedCurrencyCount > 0 ? (
        <div className="glass-card border-l-4 border-primary/30 bg-primary/5 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {excludedCurrencyCount} subscription
              {excludedCurrencyCount === 1 ? "" : "s"} in other currencies —
              upgrade to include them in your totals.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUpgradePromptOpen(true)}
            >
              Upgrade to Pro
            </Button>
          </div>
        </div>
      ) : null}

      {/* Statistics Settings - Inline with Cards */}
      <div className="glass-card border-l-4 border-primary/20 p-4">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-primary" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Include pay-as-you-go in totals
              </h3>
              <p className="text-xs text-muted-foreground">
                Toggle to include/exclude PAYG subscriptions in Monthly and
                Yearly calculations
              </p>
            </div>
          </div>
          <div className="w-full sm:w-auto sm:flex-shrink-0">
            <PaygFilterToggle
              checked={includePaygInStats}
              onCheckedChange={onIncludePaygInStatsChange}
              tooltipContent="When enabled, pay-as-you-go subscriptions will be included in your Monthly and Yearly spend calculations above."
            />
          </div>
        </div>
      </div>

      {/* Next Payment Details */}
      <Suspense fallback={<div />}>
        <NextPaymentDetails
          isOpen={isNextDetailsOpen}
          onOpenChange={setIsNextDetailsOpen}
          nextPayment={nextPayment}
          canConvertCurrency={canConvertCurrency}
          primaryCurrency={primaryCurrency}
        />
      </Suspense>

      <UpgradePrompt
        open={isUpgradePromptOpen}
        onOpenChange={setIsUpgradePromptOpen}
      />

      {/* Reconciliation Modal */}
      <Suspense fallback={<div />}>
        <ReconciliationModal
          open={isReconciliationModalOpen}
          onOpenChange={setIsReconciliationModalOpen}
        />
      </Suspense>

      {/* Monthly Spend Details Dialog */}
      <MonthlySpendDetailsDialog
        open={monthlyDialog.open}
        onOpenChange={monthlyDialog.handleOpenChange}
        subscriptions={subscriptionsForDetails}
        currentPeriod={currentPeriod}
        displayCurrency={totalCurrency}
        paymentHistory={payments}
        onEditSubscription={handleEditSubscription}
        includePaygInStats={includePaygInStats}
      />

      {/* Yearly Spend Details Dialog */}
      <YearlySpendDetailsDialog
        open={yearlyDialog.open}
        onOpenChange={yearlyDialog.handleOpenChange}
        subscriptions={subscriptionsForDetails}
        currentPeriod={currentPeriod}
        displayCurrency={totalCurrency}
        paymentHistory={payments}
        onEditSubscription={handleEditSubscription}
        includePaygInStats={includePaygInStats}
      />

      {/* Edit Subscription Dialog */}
      <AddSubscriptionDialog
        subscription={selectedSubscription}
        onComplete={handleEditComplete}
      />
    </div>
  );
};
